import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AuditLogPanel = ({ logs }) => {
  const [filterType, setFilterType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp)?.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getActionIcon = (action) => {
    if (action?.includes('Approved')) return 'CheckCircle';
    if (action?.includes('Rejected')) return 'XCircle';
    if (action?.includes('Bulk')) return 'Layers';
    if (action?.includes('Emergency')) return 'AlertTriangle';
    return 'FileText';
  };

  const getActionColor = (action) => {
    if (action?.includes('Approved')) return 'success';
    if (action?.includes('Rejected')) return 'error';
    if (action?.includes('Bulk')) return 'primary';
    if (action?.includes('Emergency')) return 'warning';
    return 'muted-foreground';
  };

  const filteredLogs = logs?.filter(log => {
    const matchesSearch = !searchQuery || 
      log?.action?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      log?.details?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    
    const matchesFilter = filterType === 'all' || log?.action?.toLowerCase()?.includes(filterType);
    
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-card rounded-2xl p-6 border border-border">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
            <Icon name="FileText" size={24} className="text-primary" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-foreground">Audit Log Trail</h2>
            <p className="text-sm text-muted-foreground">Complete history of all super admin actions</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <input
            type="text"
            placeholder="Search audit logs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
            className="flex-1 px-4 py-2 rounded-xl border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e?.target?.value)}
            className="px-4 py-2 rounded-xl border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
          >
            <option value="all">All Actions</option>
            <option value="approved">Approvals</option>
            <option value="rejected">Rejections</option>
            <option value="bulk">Bulk Actions</option>
            <option value="emergency">Emergency Overrides</option>
          </select>
        </div>
      </div>
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-card rounded-xl p-4 border border-border">
          <p className="text-sm text-muted-foreground mb-1">Total Actions</p>
          <p className="text-2xl font-bold text-foreground">{logs?.length}</p>
        </div>
        <div className="bg-success/10 rounded-xl p-4 border border-success/20">
          <p className="text-sm text-muted-foreground mb-1">Approvals</p>
          <p className="text-2xl font-bold text-success">
            {logs?.filter(l => l?.action?.includes('Approved'))?.length}
          </p>
        </div>
        <div className="bg-error/10 rounded-xl p-4 border border-error/20">
          <p className="text-sm text-muted-foreground mb-1">Rejections</p>
          <p className="text-2xl font-bold text-error">
            {logs?.filter(l => l?.action?.includes('Rejected'))?.length}
          </p>
        </div>
        <div className="bg-warning/10 rounded-xl p-4 border border-warning/20">
          <p className="text-sm text-muted-foreground mb-1">Emergency Actions</p>
          <p className="text-2xl font-bold text-warning">
            {logs?.filter(l => l?.action?.includes('Emergency'))?.length}
          </p>
        </div>
      </div>
      {/* Audit Log Entries */}
      <div className="bg-card rounded-2xl border border-border overflow-hidden">
        {filteredLogs?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="FileText" size={48} className="mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">No Audit Logs</h3>
            <p className="text-sm text-muted-foreground">
              {searchQuery || filterType !== 'all' ?'No logs match your search criteria' :'No actions have been performed yet'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border max-h-[600px] overflow-y-auto">
            {filteredLogs?.map((log) => (
              <div key={log?.id} className="p-4 hover:bg-background/50 transition-colors">
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-full bg-${getActionColor(log?.action)}/10 flex items-center justify-center flex-shrink-0`}>
                    <Icon name={getActionIcon(log?.action)} size={20} className={`text-${getActionColor(log?.action)}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <div>
                        <h4 className="font-semibold text-foreground">{log?.action}</h4>
                        <p className="text-sm text-muted-foreground">{log?.details}</p>
                      </div>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {formatTimestamp(log?.timestamp)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Icon name="User" size={12} />
                      <span>{log?.user}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Export Options */}
      <div className="bg-card rounded-2xl p-6 border border-border">
        <h3 className="text-lg font-semibold text-foreground mb-4">Export Audit Logs</h3>
        <div className="flex gap-3">
          <Button
            variant="outline"
            iconName="Download"
            onClick={() => {
              const dataStr = JSON.stringify(logs, null, 2);
              const dataBlob = new Blob([dataStr], { type: 'application/json' });
              const url = URL.createObjectURL(dataBlob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `audit-logs-${new Date()?.toISOString()}.json`;
              link?.click();
            }}
          >
            Export as JSON
          </Button>
          <Button
            variant="outline"
            iconName="FileText"
            onClick={() => {
              const csv = [
                ['Timestamp', 'Action', 'Details', 'User'],
                ...logs?.map(log => [
                  formatTimestamp(log?.timestamp),
                  log?.action,
                  log?.details,
                  log?.user
                ])
              ]?.map(row => row?.join(','))?.join('\n');
              
              const dataBlob = new Blob([csv], { type: 'text/csv' });
              const url = URL.createObjectURL(dataBlob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `audit-logs-${new Date()?.toISOString()}.csv`;
              link?.click();
            }}
          >
            Export as CSV
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AuditLogPanel;